// src/modules/nlp/SentimentLab.jsx
// Sentiment Analysis Lab — Lexicon · VADER-style · Sentence-level · Aspect-based · Live Analyzer · Model Comparison
// Tabs: input | lexicon | sentences | aspect | live | vader | models | concepts

import React, { useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  LineChart, Line, AreaChart, Area, ScatterChart, Scatter, Legend, RadarChart,
  Radar, PolarGrid, PolarAngleAxis,
} from 'recharts';
import {
  tokenizeWords, tokenizeSentences, SENTIMENT_LEXICON, INTENSIFIERS, NEGATIONS,
  analyzeSentiment, SAMPLE_TEXTS,
} from '../../utils/nlpUtils.js';
import { Panel, InfoBox } from '../../components/ui.jsx';

const ACCENT = 'var(--gold)';
import { _livePalette as P } from '../../context/paletteSync';
const TABS = ['input','lexicon','sentences','aspect','live','vader','models','concepts'];

const Btn = ({onClick,children,color=ACCENT,disabled,style})=>(
  <button onClick={onClick} disabled={disabled} style={{padding:'8px 16px',background:color+'18',border:`1px solid ${color}`,color,fontSize:'10px',cursor:disabled?'not-allowed':'pointer',borderRadius:'3px',fontFamily:'JetBrains Mono',fontWeight:700,opacity:disabled?.4:1,...style}}>{children}</button>
);
const Tag = ({children,color=ACCENT})=>(
  <span style={{padding:'2px 8px',background:color+'20',border:`1px solid ${color}44`,color,fontSize:'9px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>{children}</span>
);
const MC = ({label,value,sub,color=ACCENT})=>(
  <div style={{background:'var(--raised)',border:`1px solid ${color}22`,borderRadius:'3px',padding:'12px 14px'}}>
    <div style={{fontSize:'9px',color:'var(--t3)',letterSpacing:'.1em',marginBottom:'4px'}}>{label}</div>
    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'Syne',color}}>{value}</div>
    {sub&&<div style={{fontSize:'10px',color:'var(--t3)',marginTop:'2px'}}>{sub}</div>}
  </div>
);
const SentBadge = ({label,score})=>{
  const [c,ic]=label==='positive'?['var(--green)','▲']:label==='negative'?['var(--red)','▼']:['var(--gold)','■'];
  return (
    <span style={{display:'inline-flex',alignItems:'center',gap:'5px',padding:'3px 10px',background:c+'20',border:`1px solid ${c}44`,color:c,fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono',fontWeight:700}}>
      {ic} {label} {score!==undefined&&<span style={{opacity:.7}}>({score})</span>}
    </span>
  );
};

// Extended VADER-style rules
const BOOSTER_DICT  = {very:0.293,extremely:0.293,incredibly:0.293,absolutely:0.293,completely:0.293,totally:0.293,utterly:0.293,highly:0.293,especially:0.2,remarkably:0.2,exceptionally:0.2,quite:0.2,rather:0.1,pretty:0.1,so:0.1,super:0.2,somewhat:-0.1,kind:-0.1,sort:-0.1,barely:-0.293,hardly:-0.293,slightly:-0.1};
const SPECIAL_CASES = {'kind of':-0.5,'sort of':-0.5,'kind-of':-0.5,'sort-of':-0.5,'not really':-0.7,'not quite':-0.5,'never really':-0.7};

// Aspect keywords
const ASPECT_PATTERNS = {
  'quality':    ['quality','build','material','durability','solid','sturdy','cheap','flimsy','well-made','crafted'],
  'price':      ['price','cost','expensive','cheap','affordable','value','worth','overpriced','budget','money'],
  'service':    ['service','support','staff','team','helpful','responsive','rude','slow','customer','agent'],
  'performance':['fast','slow','speed','performance','efficient','laggy','smooth','responsive','powerful','weak'],
  'design':     ['design','look','beautiful','ugly','style','aesthetic','color','appearance','sleek','bulky'],
  'usability':  ['easy','simple','intuitive','complicated','confusing','user-friendly','hard','difficult','clear'],
};

function scoreAspect(text, keywords) {
  const sentences = tokenizeSentences(text);
  const relevant  = sentences.filter(s => keywords.some(k => s.toLowerCase().includes(k)));
  if (!relevant.length) return null;
  const scores = relevant.map(s => {
    const tokens = tokenizeWords(s);
    let score = 0;
    tokens.forEach((w,i) => {
      if (!(w in SENTIMENT_LEXICON)) return;
      const negated    = tokens.slice(Math.max(0,i-3),i).some(t=>NEGATIONS.has(t));
      const intensified= tokens.slice(Math.max(0,i-2),i).some(t=>INTENSIFIERS.has(t));
      score += SENTIMENT_LEXICON[w] * (negated?-1:1) * (intensified?1.5:1);
    });
    return score / Math.sqrt(tokens.length||1);
  });
  const avg = scores.reduce((a,b)=>a+b,0)/scores.length;
  return { score:+avg.toFixed(3), sentences:relevant, count:relevant.length,
    label: avg>0.15?'positive':avg<-0.15?'negative':'neutral' };
}

export default function SentimentLab() {
  const [tab, setTab]         = useState('input');
  const [text, setText]       = useState(SAMPLE_TEXTS.review);
  const [result, setResult]   = useState(null);
  const [liveText, setLiveText] = useState('This product is absolutely amazing! I love how easy it is to use. However, the battery life is not great and somewhat disappointing.');
  const [liveResult, setLiveResult] = useState(null);
  const [wordInspect, setWordInspect] = useState('');
  const [highlightMode, setHighlightMode] = useState('sentence'); // sentence | word | aspect

  const runAnalysis = () => {
    const r = analyzeSentiment(text);
    setResult(r);
  };

  const runLive = () => {
    setLiveResult(analyzeSentiment(liveText));
  };

  // Aspect analysis
  const aspectResults = Object.entries(ASPECT_PATTERNS).map(([aspect, keywords]) => ({
    aspect, ...( scoreAspect(text, keywords) || { score:0, sentences:[], count:0, label:'neutral' })
  }));

  // Lexicon word inspector
  const inspectWord = wordInspect.toLowerCase().trim();
  const lexValue = SENTIMENT_LEXICON[inspectWord];
  const isIntensifier = INTENSIFIERS.has(inspectWord);
  const isNegation    = NEGATIONS.has(inspectWord);

  // Sentiment arc (per sentence)
  const arc = result ? result.sentences.map((s,i)=>({ i:i+1, score:s.score, label:s.label })) : [];

  // Positive/negative word counts per sentence
  const posnegData = result ? result.sentences.map((s,i)=>{
    const tokens = tokenizeWords(s.sentence);
    const pos    = tokens.filter(w=>SENTIMENT_LEXICON[w]>0).length;
    const neg    = tokens.filter(w=>SENTIMENT_LEXICON[w]<0).length;
    return { i:i+1, pos, neg, net:pos-neg };
  }) : [];

  return (
    <div className="anim-fadeup" style={{display:'flex',flexDirection:'column',gap:'12px'}}>
      {/* Header */}
      <div style={{display:'flex',gap:'3px',background:'var(--deep)',padding:'4px',borderRadius:'3px',border:'1px solid var(--b0)',overflowX:'auto'}}>
        {TABS.map(t=>(
          <button key={t} onClick={()=>setTab(t)} style={{padding:'6px 14px',border:`1px solid ${tab===t?ACCENT:'transparent'}`,background:tab===t?ACCENT+'18':'transparent',color:tab===t?ACCENT:'var(--t3)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono',whiteSpace:'nowrap',fontWeight:tab===t?700:400}}>{t.toUpperCase()}</button>
        ))}
      </div>

      {/* ── INPUT ── */}
      {tab==='input' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="INPUT TEXT" accent={ACCENT}>
            <textarea value={text} onChange={e=>{setText(e.target.value);setResult(null);}} style={{width:'100%',minHeight:'160px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box'}}/>
            <div style={{display:'flex',gap:'8px',marginTop:'10px',flexWrap:'wrap'}}>
              {Object.entries(SAMPLE_TEXTS).map(([key])=>(
                <button key={key} onClick={()=>{setText(SAMPLE_TEXTS[key]);setResult(null);}} style={{padding:'5px 12px',border:'1px solid var(--b1)',background:'var(--raised)',color:'var(--t2)',fontSize:'10px',cursor:'pointer',borderRadius:'2px',fontFamily:'JetBrains Mono'}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=ACCENT} onMouseLeave={e=>e.currentTarget.style.borderColor='var(--b1)'}>{key}</button>
              ))}
              <Btn onClick={()=>{runAnalysis();setTab('sentences');}} color={ACCENT}>ANALYZE SENTIMENT →</Btn>
            </div>
          </Panel>
          <InfoBox color={ACCENT} icon="◎">
            <strong>Best samples:</strong> "review" has strong mixed sentiment (positive product + negative packaging). "mixed" has named entities + sentiment for aspect analysis. "story" has nuanced narrative sentiment.
          </InfoBox>
        </div>
      )}

      {/* ── LEXICON ── */}
      {tab==='lexicon' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SENTIMENT LEXICON EXPLORER" accent={ACCENT}>
            <div style={{marginBottom:'14px'}}>
              <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px',letterSpacing:'.1em'}}>INSPECT A WORD</div>
              <div style={{display:'flex',gap:'8px'}}>
                <input value={wordInspect} onChange={e=>setWordInspect(e.target.value)} placeholder="Type any word to check its sentiment value…" style={{flex:1,background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'9px 12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none'}}/>
              </div>
              {inspectWord && (
                <div style={{marginTop:'10px',padding:'14px',background:'var(--raised)',border:`1px solid ${lexValue!==undefined?'var(--gold)':isIntensifier||isNegation?'var(--violet)':'var(--b0)'}`,borderRadius:'4px'}}>
                  <div style={{display:'flex',gap:'16px',alignItems:'center',flexWrap:'wrap'}}>
                    <div style={{fontSize:'18px',fontWeight:700,fontFamily:'JetBrains Mono',color:'var(--t1)'}}>{inspectWord}</div>
                    {lexValue!==undefined && (
                      <div style={{display:'flex',gap:'10px',alignItems:'center'}}>
                        <span style={{fontSize:'9px',color:'var(--t3)'}}>SENTIMENT SCORE</span>
                        <span style={{fontSize:'22px',fontWeight:700,fontFamily:'Syne',color:lexValue>0?'var(--green)':lexValue<0?'var(--red)':'var(--gold)'}}>{lexValue>0?'+':''}{lexValue}</span>
                        <SentBadge label={lexValue>0?'positive':lexValue<0?'negative':'neutral'}/>
                      </div>
                    )}
                    {isIntensifier && <Tag color="var(--violet)">INTENSIFIER ×1.5</Tag>}
                    {isNegation    && <Tag color="var(--orange)">NEGATION ×−1</Tag>}
                    {!lexValue&&!isIntensifier&&!isNegation&&inspectWord.length>0 && <Tag color="var(--t3)">NOT IN LEXICON</Tag>}
                  </div>
                </div>
              )}
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              {/* Top positive words */}
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>TOP POSITIVE WORDS (by score)</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'5px',maxHeight:'180px',overflowY:'auto'}}>
                  {Object.entries(SENTIMENT_LEXICON).filter(([,v])=>v>0).sort((a,b)=>b[1]-a[1]).map(([word,score])=>(
                    <span key={word} onClick={()=>setWordInspect(word)} style={{padding:'3px 9px',background:'var(--ggreen)',border:`1px solid ${score>3?'var(--green)':'var(--green)55'}`,color:'var(--green)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono',cursor:'pointer',fontWeight:score>3?700:400}}>
                      {word}<span style={{opacity:.6,marginLeft:'3px'}}>+{score}</span>
                    </span>
                  ))}
                </div>
              </div>
              {/* Top negative words */}
              <div>
                <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>TOP NEGATIVE WORDS (by score)</div>
                <div style={{display:'flex',flexWrap:'wrap',gap:'5px',maxHeight:'180px',overflowY:'auto'}}>
                  {Object.entries(SENTIMENT_LEXICON).filter(([,v])=>v<0).sort((a,b)=>a[1]-b[1]).map(([word,score])=>(
                    <span key={word} onClick={()=>setWordInspect(word)} style={{padding:'3px 9px',background:'var(--gred)',border:`1px solid ${score<-3?'var(--red)':'var(--red)55'}`,color:'var(--red)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono',cursor:'pointer',fontWeight:score<-3?700:400}}>
                      {word}<span style={{opacity:.6,marginLeft:'3px'}}>{score}</span>
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </Panel>

          {/* Lexicon score distribution */}
          <Panel title="LEXICON SCORE DISTRIBUTION" accent={ACCENT}>
            {(()=>{
              const bins = [-4,-3,-2,-1,0,1,2,3,4];
              const labels = bins.slice(0,-1).map((lo,i)=>`${lo} to ${bins[i+1]}`);
              const data = labels.map((l,i)=>({
                l, count:Object.values(SENTIMENT_LEXICON).filter(v=>v>=bins[i]&&v<bins[i+1]).length,
                color:bins[i]<0?'var(--red)':bins[i]>0?'var(--green)':'var(--gold)'
              }));
              return (
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="l" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Bar dataKey="count" radius={[2,2,0,0]}>
                      {data.map((d,i)=><Cell key={i} fill={d.color} opacity={.8}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              );
            })()}
          </Panel>

          {/* Intensifiers & negations */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            <Panel title="INTENSIFIERS (×1.5)" accent="var(--violet)">
              <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                {[...INTENSIFIERS].map(w=>(
                  <span key={w} style={{padding:'3px 9px',background:'rgba(176,138,255,.12)',border:'1px solid var(--violet)44',color:'var(--violet)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}</span>
                ))}
              </div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginTop:'8px',lineHeight:1.6}}>
                "very good" → score × 1.5. "extremely bad" → |score| × 1.5. Applied within ±2 word window before the sentiment word.
              </div>
            </Panel>
            <Panel title="NEGATIONS (× −1)" accent="var(--orange)">
              <div style={{display:'flex',flexWrap:'wrap',gap:'5px'}}>
                {[...NEGATIONS].map(w=>(
                  <span key={w} style={{padding:'3px 9px',background:'rgba(255,112,64,.1)',border:'1px solid var(--orange)44',color:'var(--orange)',fontSize:'10px',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}</span>
                ))}
              </div>
              <div style={{fontSize:'10px',color:'var(--t3)',marginTop:'8px',lineHeight:1.6}}>
                "not good" → flips score to negative. Applied within ±3 word window. "not at all bad" → double negation (still flipped in our simple model).
              </div>
            </Panel>
          </div>
        </div>
      )}

      {/* ── SENTENCES ── */}
      {tab==='sentences' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          {!result && <div style={{padding:'20px',textAlign:'center'}}><Btn onClick={runAnalysis} color={ACCENT}>RUN ANALYSIS →</Btn></div>}
          {result && (
            <>
              <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:'10px'}}>
                <div style={{padding:'16px',background:'var(--raised)',border:`1px solid ${result.label==='positive'?'var(--green)':result.label==='negative'?'var(--red)':'var(--gold)'}33`,borderRadius:'4px',textAlign:'center',gridColumn:'1'}}>
                  <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'8px',letterSpacing:'.1em'}}>OVERALL</div>
                  <SentBadge label={result.label} score={result.overall}/>
                  <div style={{fontSize:'26px',fontWeight:700,fontFamily:'Syne',color:result.label==='positive'?'var(--green)':result.label==='negative'?'var(--red)':'var(--gold)',marginTop:'8px'}}>{result.overall}</div>
                </div>
                <MC label="Positive sents" value={result.pos} color="var(--green)"/>
                <MC label="Negative sents" value={result.neg} color="var(--red)"/>
                <MC label="Neutral sents"  value={result.neu} color="var(--gold)"/>
                <MC label="Total sents"    value={result.sentences.length} color="var(--cyan)"/>
              </div>

              {/* Sentiment arc */}
              <Panel title="SENTIMENT ARC — SCORE PER SENTENCE" accent={ACCENT}>
                <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'10px'}}>Reveals emotional structure of the text. Reviews: positive → problem → resolution. Stories: rising action → climax → resolution.</div>
                <ResponsiveContainer width="100%" height={180}>
                  <AreaChart data={arc}>
                    <defs>
                      <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%"  stopColor="var(--green)" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="var(--red)"   stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="i" stroke="var(--t3)" tick={{fontSize:9}} label={{value:'sentence #',position:'insideBottom',offset:-3,fill:'var(--t3)',fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip formatter={(v)=>[v,'sentiment score']}/>
                    <Area type="monotone" dataKey="score" stroke="var(--gold)" strokeWidth={2} fill="url(#sentGrad)"/>
                  </AreaChart>
                </ResponsiveContainer>
              </Panel>

              {/* Pos/neg word counts */}
              <Panel title="POSITIVE vs NEGATIVE WORDS PER SENTENCE" accent={ACCENT}>
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={posnegData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)"/>
                    <XAxis dataKey="i" stroke="var(--t3)" tick={{fontSize:9}}/>
                    <YAxis stroke="var(--t3)" tick={{fontSize:9}}/>
                    <Tooltip/>
                    <Legend wrapperStyle={{fontSize:'9px'}}/>
                    <Bar dataKey="pos" fill="var(--green)" opacity={.8} radius={[2,2,0,0]} name="Positive words" stackId="a"/>
                    <Bar dataKey="neg" fill="var(--red)"   opacity={.8} radius={[0,0,0,0]} name="Negative words" stackId="b"/>
                  </BarChart>
                </ResponsiveContainer>
              </Panel>

              {/* Sentence table */}
              <Panel title="SENTENCE-LEVEL BREAKDOWN" accent={ACCENT}>
                <div style={{maxHeight:'340px',overflowY:'auto'}}>
                  {result.sentences.map((s,i)=>{
                    const tokens = tokenizeWords(s.sentence);
                    const posWords = tokens.filter(w=>SENTIMENT_LEXICON[w]>0);
                    const negWords = tokens.filter(w=>SENTIMENT_LEXICON[w]<0);
                    const negated  = tokens.some(w=>NEGATIONS.has(w));
                    const boosted  = tokens.some(w=>INTENSIFIERS.has(w));
                    return (
                      <div key={i} style={{padding:'10px 14px',borderBottom:'1px solid var(--b0)'}}>
                        <div style={{display:'flex',gap:'10px',alignItems:'flex-start',marginBottom:'6px'}}>
                          <span style={{fontSize:'10px',color:'var(--t3)',fontFamily:'JetBrains Mono',minWidth:'22px',flexShrink:0}}>#{i+1}</span>
                          <span style={{fontSize:'11px',color:'var(--t2)',lineHeight:1.6,flex:1}}>{s.sentence}</span>
                          <div style={{display:'flex',gap:'5px',flexShrink:0,flexWrap:'wrap',justifyContent:'flex-end'}}>
                            <SentBadge label={s.label}/>
                            <Tag color={s.score>0?'var(--green)':s.score<0?'var(--red)':'var(--gold)'}>{s.score>0?'+':''}{s.score}</Tag>
                          </div>
                        </div>
                        <div style={{display:'flex',gap:'6px',flexWrap:'wrap',marginLeft:'32px'}}>
                          {posWords.slice(0,5).map(w=><span key={w} style={{fontSize:'9px',padding:'1px 6px',background:'var(--ggreen)',color:'var(--green)',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}(+{SENTIMENT_LEXICON[w]})</span>)}
                          {negWords.slice(0,5).map(w=><span key={w} style={{fontSize:'9px',padding:'1px 6px',background:'var(--gred)',color:'var(--red)',borderRadius:'2px',fontFamily:'JetBrains Mono'}}>{w}({SENTIMENT_LEXICON[w]})</span>)}
                          {negated&&<Tag color="var(--orange)">negation</Tag>}
                          {boosted&&<Tag color="var(--violet)">intensified</Tag>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── ASPECT ── */}
      {tab==='aspect' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="ASPECT-BASED SENTIMENT ANALYSIS" accent="var(--cyan)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              Instead of one overall score, ABSA assigns sentiment to specific <strong style={{color:'var(--cyan)'}}>aspects</strong> (features). "The camera is excellent but battery life is terrible" → camera: positive, battery: negative. Detects sentences relevant to each aspect via keyword matching, then scores them.
            </div>
            {!result && <Btn onClick={()=>{runAnalysis();}} color="var(--cyan)">RUN ANALYSIS FIRST →</Btn>}
          </Panel>

          {result && (
            <>
              {/* Radar chart */}
              <Panel title="ASPECT SENTIMENT RADAR" accent="var(--cyan)">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px'}}>
                  <ResponsiveContainer width="100%" height={260}>
                    <RadarChart data={aspectResults.map(a=>({aspect:a.aspect,score:a.score+2,label:a.label}))}>
                      <PolarGrid stroke="var(--b1)"/>
                      <PolarAngleAxis dataKey="aspect" tick={{fontSize:10,fill:'var(--t2)'}}/>
                      <PolarRadiusAxis angle={90} domain={[0,4]} tick={{fontSize:8,fill:'var(--t3)'}}/>
                      <Radar name="sentiment" dataKey="score" stroke="var(--cyan)" fill="var(--cyan)" fillOpacity={0.2} strokeWidth={2}/>
                      <Tooltip formatter={v=>[+(v-2).toFixed(3),'score']}/>
                    </RadarChart>
                  </ResponsiveContainer>

                  <div style={{display:'flex',flexDirection:'column',gap:'8px',justifyContent:'center'}}>
                    {aspectResults.map(({aspect,score,count,label})=>(
                      <div key={aspect} style={{padding:'10px 12px',background:'var(--raised)',border:`1px solid ${label==='positive'?'var(--green)':label==='negative'?'var(--red)':count>0?'var(--gold)':'var(--b0)'}33`,borderRadius:'3px'}}>
                        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'4px'}}>
                          <span style={{fontSize:'11px',fontWeight:700,color:'var(--t1)',textTransform:'capitalize'}}>{aspect}</span>
                          {count>0
                            ? <SentBadge label={label} score={score}/>
                            : <Tag color="var(--t3)">not found</Tag>
                          }
                        </div>
                        {count>0 && (
                          <div style={{height:'4px',background:'var(--deep)',borderRadius:'2px'}}>
                            <div style={{height:'100%',width:`${Math.min(100,Math.abs(score)/2*100)}%`,background:label==='positive'?'var(--green)':label==='negative'?'var(--red)':'var(--gold)',borderRadius:'2px',marginLeft:label==='negative'?'0':'50%',maxWidth:'50%'}}/>
                          </div>
                        )}
                        {count>0&&<div style={{fontSize:'9px',color:'var(--t3)',marginTop:'3px'}}>{count} relevant sentence{count>1?'s':''}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </Panel>

              {/* Aspect sentences */}
              {aspectResults.filter(a=>a.count>0).map(({aspect,sentences,label})=>(
                <Panel key={aspect} title={`${aspect.toUpperCase()} — relevant sentences`} accent={label==='positive'?'var(--green)':label==='negative'?'var(--red)':'var(--gold)'}>
                  {sentences.map((s,i)=>(
                    <div key={i} style={{padding:'8px 12px',background:'var(--raised)',borderRadius:'3px',marginBottom:'5px',border:'1px solid var(--b0)',fontSize:'11px',color:'var(--t2)',lineHeight:1.6}}>{s}</div>
                  ))}
                </Panel>
              ))}

              <Panel title="ABSA APPROACHES" accent="var(--cyan)">
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'10px'}}>
                  {[
                    ['Keyword Matching','var(--cyan)','Our approach: if sentence contains aspect keywords → score it. Fast, interpretable, no training. Misses implicit aspects ("this phone is a brick" = heavy/bad design without keywords).'],
                    ['Attention-based ABSA','var(--gold)','Bidirectional LSTM with attention over sentiment words conditioned on aspect. Learns aspect-sentiment dependencies. Dataset: SemEval-2014 Restaurant/Laptop reviews.'],
                    ['BERT-based ABSA','var(--violet)','Input: "[CLS] aspect [SEP] sentence [SEP]". Fine-tuned BERT classifies (aspect, sentence) pairs. SOTA ~85% F1 on SemEval benchmarks. Models: ASGCN, BERT-PT, R-GAT.'],
                  ].map(([title,color,desc])=>(
                    <div key={title} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                      <div style={{color,fontWeight:700,fontSize:'11px',marginBottom:'7px'}}>{title}</div>
                      <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.65}}>{desc}</div>
                    </div>
                  ))}
                </div>
              </Panel>
            </>
          )}
        </div>
      )}

      {/* ── LIVE ── */}
      {tab==='live' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="LIVE SENTIMENT ANALYZER" accent={ACCENT}>
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px'}}>Type text and see sentiment scored in real time with word-level highlighting.</div>
            <textarea value={liveText} onChange={e=>{setLiveText(e.target.value);setLiveResult(analyzeSentiment(e.target.value));}} style={{width:'100%',minHeight:'120px',background:'var(--deep)',border:'1px solid var(--b1)',color:'var(--t1)',padding:'12px',fontSize:'12px',fontFamily:'JetBrains Mono',borderRadius:'3px',outline:'none',resize:'vertical',lineHeight:1.7,boxSizing:'border-box',marginBottom:'10px'}}/>

            {/* Word-level highlighted view */}
            <div style={{padding:'12px',background:'var(--deep)',borderRadius:'3px',border:'1px solid var(--b0)',fontSize:'13px',lineHeight:2.2,fontFamily:'JetBrains Mono',marginBottom:'12px'}}>
              {tokenizeWords(liveText).map((w,i)=>{
                const score = SENTIMENT_LEXICON[w];
                const isNeg = NEGATIONS.has(w);
                const isInt = INTENSIFIERS.has(w);
                const bg    = score>0?'var(--ggreen)':score<0?'var(--gred)':isNeg?'rgba(255,112,64,.12)':isInt?'rgba(176,138,255,.12)':'transparent';
                const color = score>0?'var(--green)':score<0?'var(--red)':isNeg?'var(--orange)':isInt?'var(--violet)':'var(--t2)';
                const border= score||isNeg||isInt?`1px solid ${color}55`:'1px solid transparent';
                return (
                  <span key={i} title={score?`score: ${score}`:isNeg?'negation':isInt?'intensifier':''} style={{padding:'1px 4px',background:bg,border,borderRadius:'2px',color,margin:'0 1px',cursor:score||isNeg||isInt?'help':'default',fontWeight:Math.abs(score||0)>2?700:400}}>{w}</span>
                );
              })}
            </div>

            {liveText.trim() && (() => {
              const r = analyzeSentiment(liveText);
              return (
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'8px'}}>
                  <div style={{gridColumn:'1',padding:'14px',background:'var(--raised)',border:`1px solid ${r.label==='positive'?'var(--green)':r.label==='negative'?'var(--red)':'var(--gold)'}44`,borderRadius:'4px',textAlign:'center'}}>
                    <div style={{fontSize:'9px',color:'var(--t3)',marginBottom:'6px'}}>OVERALL</div>
                    <SentBadge label={r.label} score={r.overall}/>
                  </div>
                  <MC label="Pos sentences" value={r.pos} color="var(--green)"/>
                  <MC label="Neg sentences" value={r.neg} color="var(--red)"/>
                  <MC label="Neu sentences" value={r.neu} color="var(--gold)"/>
                </div>
              );
            })()}
          </Panel>

          {/* Try these */}
          <Panel title="TRY THESE EXAMPLES" accent={ACCENT}>
            <div style={{display:'flex',flexDirection:'column',gap:'6px'}}>
              {[
                ['Strong positive','This is absolutely fantastic! I love everything about it. Highly recommend to everyone.'],
                ['Strong negative','Terrible experience. The product is broken, the service was awful, and I wasted my money.'],
                ['Negation test',"This is not bad at all. I can't complain about the quality. Not the worst I've seen."],
                ['Intensifier test','Very good product! Extremely fast delivery. Incredibly helpful support team. Quite satisfied.'],
                ['Mixed review','Great camera and fast processor, but the battery life is terrible and it gets very hot.'],
                ['Sarcasm (hard)','Oh great, another product that breaks after a week. Just what I needed. Absolutely wonderful.'],
                ['Subtle positive','The device met my expectations. The build quality seems reasonable for the price point.'],
                ['Double negation','Not unhappy with the result. The service was not without its flaws, but not terrible either.'],
              ].map(([label,example])=>(
                <div key={label} onClick={()=>{setLiveText(example);setLiveResult(analyzeSentiment(example));}} style={{padding:'8px 12px',background:'var(--raised)',border:'1px solid var(--b0)',borderRadius:'3px',cursor:'pointer',transition:'all .15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--gold)';}} onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--b0)';}}>
                  <div style={{fontSize:'10px',fontWeight:700,color:'var(--gold)',marginBottom:'2px'}}>{label}</div>
                  <div style={{fontSize:'10px',color:'var(--t3)'}}>{example}</div>
                </div>
              ))}
            </div>
          </Panel>
        </div>
      )}

      {/* ── VADER ── */}
      {tab==='vader' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="VADER — VALENCE AWARE DICTIONARY AND SENTIMENT REASONER" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'14px',lineHeight:1.7}}>
              Hutto & Gilbert (2014). Designed specifically for social media. F1≈0.96 on tweet sentiment. Key innovations: punctuation rules, ALL CAPS amplification, emoji handling, degree modifiers, conjunctions ("but" shifts emphasis to the clause after it).
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              {[
                {rule:'Punctuation',icon:'!!!',color:'var(--orange)',desc:'Each "!" adds 0.292 to score magnitude. "Good!!!" scores higher than "Good". "???" adds negativity. Max 3 punctuation marks counted.'},
                {rule:'ALL CAPS',icon:'GREAT',color:'var(--gold)',desc:'"GREAT" scores higher than "great" by a booster amount (0.733). Reflects emphasis in informal text. Only applies to words already in lexicon.'},
                {rule:'"But" shift',icon:'but',color:'var(--violet)',desc:'"but" is a contrastive conjunction. VADER weights the sentiment after "but" more heavily (50% discount before "but", 50% increase after).'},
                {rule:'Emoji',icon:'😊',color:'var(--cyan)',desc:'Emoji mapped to sentiment scores: 😊=+1.4, 😢=−1.9, 🔥=+1.0, 💀=−1.6. Crucial for social media where emoji carry primary sentiment.'},
                {rule:'Degree modifiers',icon:'kind of',color:'var(--green)',desc:'"kind of good" reduces score. "sort of" dampens. "almost" reduces. "hardly" near-negates. Corpus-validated booster/dampener values.'},
                {rule:'Contraction handling',icon:"n\'t",color:'var(--red)',desc:'"isn\'t","wasn\'t","can\'t","won\'t" all treated as negation triggers. Handles informal contractions that simple tokenizers might split incorrectly.'},
              ].map(({rule,icon,color,desc})=>(
                <div key={rule} style={{padding:'12px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                  <div style={{display:'flex',justifyContent:'space-between',marginBottom:'6px'}}>
                    <span style={{fontSize:'11px',fontWeight:700,color}}>{rule}</span>
                    <code style={{fontSize:'13px',fontFamily:'JetBrains Mono',color,background:color+'18',padding:'2px 8px',borderRadius:'2px'}}>{icon}</code>
                  </div>
                  <div style={{fontSize:'10px',color:'var(--t3)',lineHeight:1.6}}>{desc}</div>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="VADER OUTPUT — compound, pos, neg, neu" accent="var(--orange)">
            <div style={{fontSize:'11px',color:'var(--t3)',marginBottom:'12px',lineHeight:1.7}}>
              VADER returns 4 scores: <span style={{color:'var(--green)'}}>pos</span> (0-1), <span style={{color:'var(--red)'}}>neg</span> (0-1), <span style={{color:'var(--gold)'}}>neu</span> (0-1), <span style={{color:'var(--cyan)'}}>compound</span> (−1 to +1). Compound ≥ 0.05 → positive; ≤ −0.05 → negative. pos+neg+neu ≈ 1.0.
            </div>
            {[
              {text:'I absolutely love this! It is amazing!!!', compound:0.93, pos:0.6, neg:0.0, neu:0.4},
              {text:'Terrible, horrible, awful disaster. Worst ever.', compound:-0.91, pos:0.0, neg:0.75, neu:0.25},
              {text:'The food here is not bad at all.', compound:0.43, pos:0.19, neg:0.0, neu:0.81},
              {text:'The movie was okay, not great but watchable.', compound:-0.02, pos:0.12, neg:0.15, neu:0.73},
              {text:'😊 Great product! Fast delivery 👍', compound:0.76, pos:0.55, neg:0.0, neu:0.45},
            ].map(({text,compound,pos,neg,neu})=>(
              <div key={text} style={{padding:'10px 12px',background:'var(--raised)',borderRadius:'3px',marginBottom:'6px',border:'1px solid var(--b0)'}}>
                <div style={{fontSize:'11px',color:'var(--t2)',marginBottom:'8px',fontStyle:'italic'}}>"{text}"</div>
                <div style={{display:'flex',gap:'10px',flexWrap:'wrap'}}>
                  {[['compound',compound,'var(--cyan)'],['pos',pos,'var(--green)'],['neg',neg,'var(--red)'],['neu',neu,'var(--gold)']].map(([l,v,c])=>(
                    <div key={l} style={{display:'flex',alignItems:'center',gap:'5px'}}>
                      <span style={{fontSize:'9px',color:'var(--t3)'}}>{l}:</span>
                      <span style={{fontSize:'12px',fontWeight:700,fontFamily:'JetBrains Mono',color:c}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </Panel>
        </div>
      )}

      {/* ── MODELS ── */}
      {tab==='models' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <Panel title="SENTIMENT MODELS — BENCHMARK COMPARISON" accent="var(--violet)">
            {(()=>{
              const benchmarks=[
                {model:'Lexicon (ours)',    sst2:72, imdb:75, twitter:71, amazon:73, speed:'<1ms',  params:'N/A',     color:'var(--t3)'},
                {model:'VADER',            sst2:74, imdb:77, twitter:86, amazon:76, speed:'<1ms',  params:'N/A',     color:'var(--gold)'},
                {model:'TextBlob',         sst2:69, imdb:72, twitter:68, amazon:70, speed:'<1ms',  params:'N/A',     color:'var(--orange)'},
                {model:'Naive Bayes',      sst2:80, imdb:83, twitter:78, amazon:82, speed:'<5ms',  params:'~1M',     color:'var(--cyan)'},
                {model:'LSTM',             sst2:87, imdb:90, twitter:84, amazon:88, speed:'10ms',  params:'~5M',     color:'var(--green)'},
                {model:'DistilBERT',       sst2:91, imdb:92, twitter:87, amazon:91, speed:'50ms',  params:'66M',     color:'var(--violet)'},
                {model:'BERT-base',        sst2:93, imdb:94, twitter:89, amazon:93, speed:'100ms', params:'110M',    color:'var(--violet)'},
                {model:'RoBERTa',          sst2:96, imdb:96, twitter:92, amazon:95, speed:'120ms', params:'125M',    color:'#f472b6'},
                {model:'GPT-4 (zero-shot)',sst2:95, imdb:95, twitter:91, amazon:94, speed:'1-3s',  params:'~1T',     color:'#34d399'},
              ];
              return (
                <>
                  <div style={{overflowX:'auto',marginBottom:'14px'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:'11px'}}>
                      <thead><tr style={{background:'var(--deep)'}}>
                        {['Model','SST-2','IMDB','Twitter','Amazon','Speed','Params'].map(h=>(
                          <th key={h} style={{padding:'8px 12px',textAlign:h==='Model'?'left':'center',color:'var(--t3)',fontSize:'9px',letterSpacing:'.08em',borderBottom:'1px solid var(--b0)',whiteSpace:'nowrap'}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {benchmarks.map(({model,sst2,imdb,twitter,amazon,speed,params,color},i)=>(
                          <tr key={model} style={{borderBottom:'1px solid var(--b0)',background:i%2===0?'transparent':'rgba(0,0,0,.05)'}}>
                            <td style={{padding:'8px 12px',color,fontWeight:600,fontFamily:'JetBrains Mono'}}>{model}</td>
                            {[sst2,imdb,twitter,amazon].map((v,j)=>(
                              <td key={j} style={{padding:'8px 12px',textAlign:'center',color:v>=90?'var(--green)':v>=80?'var(--gold)':'var(--t3)',fontFamily:'JetBrains Mono',fontWeight:v>=90?700:400}}>{v}%</td>
                            ))}
                            <td style={{padding:'8px 12px',textAlign:'center',color:'var(--t3)',fontSize:'10px'}}>{speed}</td>
                            <td style={{padding:'8px 12px',textAlign:'center',color:'var(--t3)',fontSize:'10px'}}>{params}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={benchmarks} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--b0)" horizontal={false}/>
                      <XAxis type="number" domain={[60,100]} stroke="var(--t3)" tick={{fontSize:9}} tickFormatter={v=>`${v}%`}/>
                      <YAxis type="category" dataKey="model" stroke="var(--t3)" tick={{fontSize:9}} width={110}/>
                      <Tooltip formatter={v=>[`${v}%`]}/>
                      <Legend wrapperStyle={{fontSize:'9px'}}/>
                      <Bar dataKey="sst2"    fill="var(--cyan)"   opacity={.8} name="SST-2"/>
                      <Bar dataKey="twitter" fill="var(--violet)" opacity={.8} name="Twitter"/>
                    </BarChart>
                  </ResponsiveContainer>
                </>
              );
            })()}
          </Panel>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Why Lexicon Methods Still Matter',color:'var(--gold)',desc:'No training data needed. Fully interpretable — you can trace every word contribution. Instant inference. Easy to customize by adding domain-specific words. Baseline for any new sentiment task. Still used in finance (FinBERT baseline), clinical NLP, and low-resource languages.'},
              {title:'Transfer Learning Revolution',color:'var(--violet)',desc:'Pre-train on massive corpus (MLM, CLM) → fine-tune on small labeled dataset. SST-2 has only 67K training examples but BERT achieves 93%. Before BERT, training from scratch needed millions. Now 1000 labeled examples often enough.'},
              {title:'Dataset Biases',color:'var(--orange)',desc:'SST-2 (movie reviews) model transferred to product reviews: drops 10-15% F1. Twitter sentiment model fails on clinical notes. Domain mismatch is the #1 real-world failure mode. Solution: domain-adaptive pre-training (DAPT) on target domain text.'},
              {title:'Beyond Binary Classification',color:'var(--red)',desc:'Binary (pos/neg) → fine-grained (1-5 stars) → ordinal → aspect-level → emotion detection (joy, fear, anger, sadness, surprise, disgust) → opinion triplets (aspect, opinion, sentiment). GoEmotions: 27 emotion categories from Reddit. SemEval ABSA tasks.'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── CONCEPTS ── */}
      {tab==='concepts' && (
        <div style={{display:'flex',flexDirection:'column',gap:'12px'}}>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
            {[
              {title:'Sentiment vs Subjectivity',color:ACCENT,desc:'Sentiment: positive/negative/neutral polarity. Subjectivity: objective fact vs subjective opinion. "The phone has 12GB RAM" (objective) vs "The phone is great" (subjective, positive). Both dimensions matter — extract only subjective sentences for sentiment analysis. OpinionFinder separates these.'},
              {title:'Valence Shifting',color:'var(--orange)',desc:'Context modifiers that shift sentiment polarity: negations ("not good"), intensifiers ("very bad"), diminishers ("somewhat good"), adversative conjunctions ("good but expensive"). SenticNet models these as hourglass-shaped sentiment shifts in semantic space.'},
              {title:'Sarcasm & Irony',color:'var(--red)',desc:'"Oh great, another Monday." — positive words, negative sentiment. Hardest problem in sentiment analysis. Requires world knowledge, context, discourse. Deep sarcasm detection: ~70% accuracy vs ~90% for standard sentiment. BERT + cue words (oh, great, yeah right) helps somewhat.'},
              {title:'Implicit Sentiment',color:'var(--violet)',desc:'No explicit opinion words. "The bag handle broke after 2 days" → negative. "The restaurant was empty on a Saturday night" → negative (implication). Requires inference about world knowledge. Graph-based approaches, knowledge graphs (ConceptNet), commonsense reasoning (ATOMIC).'},
              {title:'Multilingual Sentiment',color:'var(--cyan)',desc:'Many languages lack large labeled datasets. Solutions: (1) Machine translate to English → English model; (2) Cross-lingual BERT (mBERT, XLM-R) zero-shot transfer; (3) Multilingual lexicons (SentiWordNet, NRC Emotion Lexicon in 105 languages). XLM-R achieves competitive cross-lingual transfer.'},
              {title:'Real-World Applications',color:'var(--green)',desc:'Brand monitoring: track public sentiment about products in real time (Brandwatch, Sprout Social). Finance: news sentiment → stock price prediction. CX: support ticket routing by urgency/sentiment. Healthcare: patient feedback → service improvement. Politics: election prediction from social media (limited accuracy).'},
            ].map(({title,color,desc})=>(
              <div key={title} style={{padding:'16px',background:'var(--raised)',border:`1px solid ${color}33`,borderRadius:'4px'}}>
                <div style={{color,fontWeight:700,fontSize:'13px',marginBottom:'8px'}}>{title}</div>
                <div style={{fontSize:'11px',color:'var(--t3)',lineHeight:1.7}}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
